<template>
  <div class="dealer-overview">
    <div class="row-2">
      <!-- 合约概况 -->
      <div class="dealer-col dealer-col--contract">
        <h3 class="section-title"><span class="title-bar" />合约概况</h3>
        <div class="section-summary section-summary--blue">
          <span class="summary-label">在合作经销商总数</span>
          <span class="summary-num">{{ fmtNum(d.contract.totalCooperating.value) }}</span>
          <span class="summary-unit">{{ d.contract.totalCooperating.unit }}</span>
        </div>

        <div
          v-for="card in contractStatusCards"
          :key="card.key"
          class="status-card"
        >
          <div class="status-card-head">
            <span class="status-icon" :class="'status-icon--' + card.iconTheme">
              <dashboard-icons :name="card.icon" size="18" color="#fff" />
            </span>
            <span class="status-card-title">{{ card.title }}</span>
          </div>
          <div
            v-for="(section, sIdx) in statusSections(card.data)"
            :key="section.key"
            class="status-block"
          >
            <div v-if="sIdx > 0" class="status-divider" role="separator" />
            <div class="status-block-row">
              <div class="status-main">
                <div class="status-main-label">
                  <span class="status-bullet" />
                  <span class="group-title">{{ section.totalLabel }}</span>
                </div>
                <div class="big-value">
                  <span class="num">{{ fmtNum(section.total.count) }}</span>
                  <span class="unit">家</span>
                </div>
              </div>
              <div class="status-total-ratio">
                <span class="field-label">占比</span>
                <span class="field-value">{{ fmtRatio(section.total.ratio) }}%</span>
              </div>
              <div class="status-sub-list">
                <div v-for="sub in section.items" :key="sub.label" class="sub-row">
                  <span class="sub-label">{{ sub.label }}</span>
                  <span class="sub-value">
                    <span class="field-value">{{ fmtNum(sub.count) }}</span>
                    <span class="field-unit">家</span>
                  </span>
                </div>
              </div>
              <div class="status-sub-ratios">
                <div v-for="sub in section.items" :key="sub.label + '-r'" class="sub-ratio">
                  <span class="field-label">占比</span>
                    <span class="field-value">{{ fmtRatio(sub.ratio) }}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 经销商资金概况 -->
      <div class="dealer-col dealer-col--fund">
        <h3 class="section-title"><span class="title-bar title-bar--purple" />经销商资金概况</h3>
        <div class="section-summary section-summary--purple">
          <span class="summary-label">现存经销商资金总额</span>
          <span class="summary-num">{{ fmtAmount(d.fund.totalBalance.value) }}</span>
          <span class="summary-unit">{{ d.fund.totalBalance.unit }}</span>
        </div>

        <div v-for="card in fundCollectionCards" :key="card.key" class="fund-card">
          <div class="fund-card-head">
            <span class="status-icon status-icon--purple">
              <dashboard-icons name="wallet" size="18" color="#fff" />
            </span>
            <span class="status-card-title">{{ card.title }}</span>
          </div>
          <div class="fund-card-body">
            <div class="fund-content">
              <div class="fund-total-row">
                <div class="fund-total-lead">
                  <span class="status-bullet status-bullet--purple" />
                  <span class="fund-total-label">金额合计</span>
                </div>
                <span class="fund-total-amount">
                  <span class="num">{{ fmtAmount(card.data.total.value) }}</span>
                  <span class="unit">元</span>
                </span>
              </div>
              <div class="fund-detail-box">
                <div class="fund-detail-line">
                  <span class="detail-label">新商打款</span>
                  <span class="detail-amount">
                    <span class="field-value">{{ fmtAmount(card.data.newPayment.value) }}</span>
                    <span class="field-unit">元</span>
                  </span>
                  <span class="detail-ratio detail-ratio--blue">占比 {{ fmtRatio(card.data.newPayment.ratio) }}%</span>
                </div>
                <div class="fund-detail-line">
                  <span class="detail-label">老商补款</span>
                  <span class="detail-amount">
                    <span class="field-value">{{ fmtAmount(card.data.oldPayment.value) }}</span>
                    <span class="field-unit">元</span>
                  </span>
                  <span class="detail-ratio detail-ratio--orange">占比 {{ fmtRatio(card.data.oldPayment.ratio) }}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus'
import DashboardIcons from './DashboardIcons.vue'
import { getDashboardDealerOverview } from '@/api/dashboard'

function emptyRoleGroup(labels) {
  return {
    total: { count: 0, ratio: 0 },
    items: labels.map(label => ({ label, count: 0, ratio: 0 })),
  }
}

function emptyStatusCard() {
  return {
    operators: emptyRoleGroup(['城市运营商', '行业运营商']),
    promotion: emptyRoleGroup(['一级推广', '二级推广', '三级推广']),
  }
}

function emptyCollection() {
  return {
    total: { value: 0 },
    newPayment: { value: 0, ratio: 0 },
    oldPayment: { value: 0, ratio: 0 },
  }
}

function emptyDealerOverview() {
  return {
    contract: {
      totalCooperating: { value: 0, unit: '个' },
      active: emptyStatusCard(),
      terminated: emptyStatusCard(),
    },
    fund: {
      totalBalance: { value: 0, unit: '元' },
      yearCollection: emptyCollection(),
      monthCollection: emptyCollection(),
    },
  }
}

export default {
  name: 'DashboardDealerOverview',
  components: { DashboardIcons },
  data() {
    return { d: emptyDealerOverview() }
  },
  mounted() {
    this.loadData()
  },
  computed: {
    contractStatusCards() {
      return [
        {
          key: 'active',
          title: '在合作的',
          icon: 'heart',
          iconTheme: 'blue',
          data: (this.d.contract && this.d.contract.active) || emptyStatusCard(),
        },
        {
          key: 'terminated',
          title: '已解约的',
          icon: 'contractOff',
          iconTheme: 'orange',
          data: (this.d.contract && this.d.contract.terminated) || emptyStatusCard(),
        },
      ]
    },
    fundCollectionCards() {
      return [
        { key: 'year', title: '经销商回款（本年）', data: (this.d.fund && this.d.fund.yearCollection) || emptyCollection() },
        { key: 'month', title: '经销商回款（本月）', data: (this.d.fund && this.d.fund.monthCollection) || emptyCollection() },
      ]
    },
  },
  methods: {
    async loadData() {
      try {
        const res = await getDashboardDealerOverview()
        if (res && res.data) {
          this.d = res.data
        }
      } catch (e) {
        ElMessage.error(e.message || '加载经销商概况失败')
      }
    },
    fmtNum(n) {
      const v = Number(n)
      if (Number.isNaN(v)) return '0'
      return v.toLocaleString('en-US')
    },
    fmtAmount(n) {
      const v = Number(n)
      if (Number.isNaN(v)) return '0.00'
      return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    fmtRatio(n) {
      const v = Number(n)
      if (Number.isNaN(v)) return '0.00'
      return v.toFixed(2)
    },
    statusSections(data) {
      const src = data || emptyStatusCard()
      const operators = src.operators || emptyRoleGroup(['城市运营商', '行业运营商'])
      const promotion = src.promotion || emptyRoleGroup(['一级推广', '二级推广', '三级推广'])
      return [
        {
          key: 'operators',
          totalLabel: '运营商合计',
          total: operators.total,
          items: operators.items || [],
        },
        {
          key: 'promotion',
          totalLabel: '推广中心合计',
          total: promotion.total,
          items: promotion.items || [],
        },
      ]
    },
  },
}
</script>

<style scoped>
/* 文字层级：与租赁业务概况一致 */
.field-label {
  color: #8c8c8c;
  font-size: 13px;
  font-weight: 400;
}

.field-value {
  color: #262626;
  font-size: 14px;
  font-weight: 600;
}

.field-unit {
  color: #262626;
  font-size: 14px;
  font-weight: 400;
}

.dealer-overview {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.row-2 {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  width: 100%;
}

.dealer-col {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.section-title {
  display: flex;
  align-items: center;
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  line-height: 1.4;
}

.title-bar {
  display: inline-block;
  width: 4px;
  height: 16px;
  margin-right: 8px;
  background: #1890ff;
  border-radius: 1px;
  flex-shrink: 0;
}

.title-bar--purple {
  background: #9254de;
}

.section-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px 10px;
  padding: 12px 14px;
  border-radius: 8px;
  background: #f0f6ff;
}

.section-summary--purple {
  background: #f6f0ff;
}

.summary-label {
  font-size: 15px;
  font-weight: 600;
  color: #262626;
}

.summary-num {
  font-size: clamp(25px, 2.5vw, 29px);
  font-weight: 600;
  line-height: 1.15;
}

.section-summary--blue .summary-num {
  color: #1890ff;
}

.section-summary--purple .summary-num {
  color: #1890ff;
}

.summary-unit {
  font-size: 14px;
  font-weight: 400;
  color: #8c8c8c;
  padding-bottom: 3px;
}

/* 合约状态卡 */
.status-card,
.fund-card {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 14px 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.dealer-col--contract .status-card,
.dealer-col--fund .fund-card {
  flex: 1 1 0;
  padding-bottom: 12px;
}

.dealer-col--fund .fund-card .fund-card-body {
  flex: 1;
  min-height: 0;
}

.status-card-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}

.fund-card-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 8px;
  margin-bottom: 0;
  border-bottom: 1px solid #f0f0f0;
}

.status-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.status-icon--blue {
  background: linear-gradient(135deg, #69b1ff, #1890ff);
}

.status-icon--orange {
  background: linear-gradient(135deg, #ffc069, #fa8c16);
}

.status-icon--purple {
  background: linear-gradient(135deg, #b37feb, #9254de);
}

.status-card-title {
  font-size: 15px;
  font-weight: 600;
  color: #262626;
}

.status-divider {
  height: 1px;
  background: #f0f0f0;
  margin: 18px 0;
}

.status-block-row {
  display: grid;
  grid-template-columns: minmax(140px, auto) minmax(64px, auto) minmax(0, 1fr) auto;
  gap: 14px 28px;
  align-items: start;
}

.status-main {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.status-main-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.status-bullet {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #1890ff;
  display: inline-block;
  flex-shrink: 0;
}

.group-title {
  font-size: 14px;
  font-weight: 400;
  color: #262626;
  line-height: 1.3;
}

.big-value {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
}

.big-value .num {
  font-size: clamp(19px, 2vw, 23px);
  font-weight: 600;
  color: #262626;
  line-height: 1.15;
}

.big-value--purple .num {
  color: #262626;
}

.big-value .unit {
  font-size: 14px;
  font-weight: 400;
  color: #262626;
  padding-bottom: 1px;
}

.status-total-ratio {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  padding-top: 2px;
  white-space: nowrap;
}

.status-total-ratio .field-label,
.status-total-ratio .field-value {
  font-size: 13px;
}

.status-sub-list,
.status-sub-ratios {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 2px;
  min-width: 0;
}

.sub-row {
  display: grid;
  grid-template-columns: 80px auto;
  gap: 4px 12px;
  align-items: baseline;
  min-width: 0;
  white-space: nowrap;
  line-height: 1.5;
  min-height: 22px;
}

.sub-ratio {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  white-space: nowrap;
  line-height: 1.5;
  min-height: 22px;
}

.sub-label {
  font-size: 14px;
  font-weight: 400;
  color: #595959;
}

.sub-value {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
}

.sub-value .field-value {
  font-weight: 600;
  font-size: 14px;
}

.sub-ratio .field-label,
.sub-ratio .field-value {
  font-size: 13px;
}

/* 资金回款卡 */
.fund-card-body {
  padding-top: 10px;
}

.fund-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  flex: 1;
  min-height: 0;
}

.dealer-col--fund .fund-content {
  margin-top: 10px;
}

.dealer-col--fund .fund-detail-box {
  flex: 1;
  justify-content: center;
}

.fund-total-row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 12px;
  padding: 2px 0;
}

.fund-total-lead {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.status-bullet--purple {
  background: #9254de;
}

.fund-total-label {
  font-size: 14px;
  font-weight: 600;
  color: #9254de;
  white-space: nowrap;
}

.fund-total-amount {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
}

.fund-total-amount .num {
  font-size: clamp(21px, 2.2vw, 25px);
  font-weight: 600;
  color: #262626;
  line-height: 1.15;
}

.fund-total-amount .unit {
  font-size: 14px;
  font-weight: 400;
  color: #262626;
  padding-bottom: 1px;
}

.fund-detail-box {
  background: #f0f6ff;
  border-radius: 6px;
  padding: 14px 18px 10px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-width: 0;
  min-height: 72px;
  box-sizing: border-box;
}

.fund-detail-line {
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr) auto;
  column-gap: 20px;
  row-gap: 4px;
  align-items: baseline;
  line-height: 1.45;
  min-height: 24px;
}

.fund-detail-box .detail-label {
  font-size: 15px;
  font-weight: 400;
  color: #595959;
  white-space: nowrap;
}

.detail-amount {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  min-width: 0;
}

.fund-detail-box .detail-amount .field-value {
  font-size: 15px;
  font-weight: 600;
}

.fund-detail-box .detail-amount .field-unit {
  font-size: 15px;
}

.fund-detail-box .detail-ratio {
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
}

.detail-ratio--blue {
  color: #1890ff;
}

.detail-ratio--orange {
  color: #fa8c16;
}

@media (max-width: 900px) {
  .row-2 {
    grid-template-columns: 1fr;
  }

  .status-block-row {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 520px) {
  .sub-row,
  .fund-detail-line {
    grid-template-columns: 1fr;
    gap: 4px;
  }
}
</style>
